import React, { useEffect, useState, useContext } from 'react';
import AppSubMenu from './AppSubMenu';
import { menuService } from '../services/menuService';
import { ProgressSpinner } from 'primereact/progressspinner';
import { LayoutContext } from './context/layoutcontext';
import impersonationService from '../services/impersonationService';
import { leadBroadcastService } from '../services/leadBroadcastService';

/**
 * Walk the menu model and ensure a "Notifications" item exists, with the
 * given unseen count rendered as a PrimeReact badge. Inserted just BEFORE
 * the Support item if found, else appended at the end of the top group.
 * If the item already exists (e.g. menuService grew a static entry), the
 * badge count is updated in-place rather than duplicated.
 */
const injectNotificationsItem = (menuModel, unseenCount) => {
    if (!Array.isArray(menuModel) || menuModel.length === 0) return menuModel;

    const cloned = menuModel.map((group) => ({
        ...group,
        items: Array.isArray(group.items) ? [...group.items] : group.items,
    }));

    const targetGroup = cloned[0];
    if (!targetGroup || !Array.isArray(targetGroup.items)) return cloned;

    const notifItem = {
        label: 'Notifications',
        icon: 'pi pi-fw pi-bell',
        to: '/app/notifications',
        // PrimeReact PanelMenu accepts `badge` on a leaf item — undefined
        // when count is 0 so we don't render an empty bubble.
        badge: unseenCount > 0 ? String(unseenCount) : undefined,
        // Used to find + update the existing entry on subsequent renders.
        _key: 'notifications',
    };

    const existingIdx = targetGroup.items.findIndex(
        (it) => it && (it._key === 'notifications' || it.to === '/app/notifications')
    );
    if (existingIdx !== -1) {
        targetGroup.items[existingIdx] = { ...targetGroup.items[existingIdx], ...notifItem };
        return cloned;
    }

    // Insert just before Support, falling back to end-of-list.
    const supportIdx = targetGroup.items.findIndex(
        (it) => it && (it.label === 'Support' || it.to === '/app/support/my-issues')
    );
    if (supportIdx !== -1) {
        targetGroup.items.splice(supportIdx, 0, { separator: true }, notifItem);
    } else {
        targetGroup.items.push({ separator: true }, notifItem);
    }
    return cloned;
};

const AppMenu = () => {
    const [menuModel, setMenuModel] = useState([]);
    const [loading, setLoading] = useState(true);
    const [unseenCount, setUnseenCount] = useState(0);
    const { setSelectedLoanUrl } = useContext(LayoutContext);

    // Fetch unseen claim-rejection count. Cheap (single COUNT). Safe to call
    // on every focus event. 401/403 swallowed silently — sidebar is best-effort.
    const refreshUnseenCount = async () => {
        try {
            const result = await leadBroadcastService.listClaimRejections({
                page: 1, limit: 1, only_unseen: true,
            });
            const total = result?.data?.unseen_total ?? 0;
            setUnseenCount(total);
        } catch {
            // best-effort only
        }
    };

    useEffect(() => {
        console.log('AppMenu mounting, loading menu items...');

        // Set up a timeout to prevent infinite loading
        const menuLoadingTimeout = setTimeout(() => {
            console.warn('Menu loading timeout - forcing completion');
            setLoading(false);
            // Set fallback menu if still loading
            if (loading) {
                setMenuModel([
                    {
                        label: '',
                        items: [
                            { label: 'Dashboard', icon: 'pi pi-fw pi-home', to: '/app/dashboard' },
                            { separator: true },
                            { label: 'Credits', icon: 'pi pi-fw pi-wallet', to: '/app/credit-wallet' }
                        ]
                    }
                ]);
            }
        }, 8000); // 8 second timeout for menu

        loadMenuItems().then(() => {
            clearTimeout(menuLoadingTimeout);
            // Fire-and-forget unread badge fetch.
            refreshUnseenCount();
        }).catch(() => {
            clearTimeout(menuLoadingTimeout);
        });

        // Subscribe to impersonation state changes to reload menu
        const unsubscribe = impersonationService.onAuthStateChange(() => {
            console.log('Auth state changed, reloading menu...');
            loadMenuItems();
            refreshUnseenCount();
        });

        // Refresh unseen count when the tab regains focus — covers the case
        // where the user comes back from another tab after a teammate's
        // regenerate auto-rejected their claim.
        const onFocus = () => refreshUnseenCount();
        window.addEventListener('focus', onFocus);

        // Other components (NotificationsPage's mark-as-read, the toast
        // notifier's mark-seen) dispatch this event so the badge clears
        // immediately instead of waiting for a focus event.
        const onRefresh = () => refreshUnseenCount();
        window.addEventListener('notifications-refresh', onRefresh);

        return () => {
            clearTimeout(menuLoadingTimeout);
            unsubscribe();
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('notifications-refresh', onRefresh);
        };
    }, []); // Only load once on mount, but will reload on auth state changes

    const loadMenuItems = async () => {
        try {
            setLoading(true);
            console.log('Loading menu items...');

            // Get current user from localStorage (this will be the impersonated user if impersonating)
            const userData = localStorage.getItem('user');
            let userRole = 'user'; // default role

            if (userData) {
                try {
                    const user = JSON.parse(userData);

                    // Check if user has roles array in the user object
                    if (user.roles && Array.isArray(user.roles) && user.roles.length > 0) {
                        const roles = user.roles.map(r => typeof r === 'string' ? r : r.name);
                        if (roles.includes('tenant_admin')) {
                            userRole = 'tenant_admin';
                        } else if (roles.includes('admin')) {
                            userRole = 'admin';
                        } else if (roles.includes('manager')) {
                            userRole = 'manager';
                        } else {
                            userRole = roles[0];
                        }
                    } else {
                        // Fallback to userRoles in localStorage (for backward compatibility)
                        const userRolesData = localStorage.getItem('userRoles');
                        if (userRolesData) {
                            const roles = JSON.parse(userRolesData);
                            if (Array.isArray(roles) && roles.length > 0) {
                                if (roles.includes('tenant_admin')) {
                                    userRole = 'tenant_admin';
                                } else if (roles.includes('admin')) {
                                    userRole = 'admin';
                                } else if (roles.includes('manager')) {
                                    userRole = 'manager';
                                } else {
                                    userRole = roles[0];
                                }
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error parsing user data:', error);
                }
            }

            console.log('[AppMenu] User role:', userRole);

            // Use role-based menu loading
            const menuItems = await menuService.getMenuItemsByRole(userRole, setSelectedLoanUrl, false);

            console.log('Menu items loaded:', menuItems);
            setMenuModel(menuItems || []);
        } catch (error) {
            console.error('Error loading menu:', error);
            // Simple fallback if API fails
            setMenuModel([
                {
                    label: '',
                    items: [
                        { label: 'Dashboard', icon: 'pi pi-fw pi-home', to: '/app/dashboard' },
                        { separator: true },
                        { label: 'Credits', icon: 'pi pi-fw pi-wallet', to: '/app/credit-wallet' }
                    ]
                }
            ]);
        } finally {
            setLoading(false);
            console.log('Menu loading completed');
        }
    };

    if (loading) {
        return (
            <div className="flex align-items-center justify-content-center p-4">
                <ProgressSpinner style={{ width: '30px', height: '30px' }} strokeWidth="4" />
            </div>
        );
    }

    return <AppSubMenu model={injectNotificationsItem(menuModel, unseenCount)} />;
};

export default AppMenu;